/**
 * 방 정보 페이지 - 멤버 목록, 이름 변경, 온라인
 */
import { apiGetRoom } from '../lib/api';
import { escapeHtml, getAvatarGradient } from '../lib/utils';
import { toast } from '../lib/toast';
import type { AppStateManager } from '../app';

let _app: AppStateManager;
let _roomId: string;
let _myRole: string;

export function renderRoomInfoPage(container: HTMLElement, app: AppStateManager, roomId: string, myRole: string): void {
  _app = app; _roomId = roomId; _myRole = myRole;
  loadRoomInfo();
}

async function loadRoomInfo(): Promise<void> {
  const el = document.getElementById('room-info-content')!;
  el.innerHTML = '<div class="flex items-center justify-center py-20"><div class="animate-spin rounded-full h-8 w-8 border-2 border-kalpha-400 border-t-transparent"></div></div>';

  const res = await apiGetRoom(_roomId);
  if (!res.success || !res.data) { el.innerHTML = '<p class="empty-state text-sm text-center py-20">불러오기 실패</p>'; return; }

  const room = res.data;
  const members = (room as any).members || [];
  const owner = members.find((m: any) => m.role === 'owner');
  const subAdmins = members.filter((m: any) => m.role === 'sub_admin');
  const regulars = members.filter((m: any) => m.role === 'member');

  el.innerHTML = `
    <div class="space-y-5">
      <!-- 방 이름 -->
      <div>
        <div class="section-header mb-2">채팅방 정보</div>
        <div class="surface-secondary rounded-xl p-4">
          <div class="flex gap-2 ${_myRole === 'owner' ? '' : 'pointer-events-none'}">
            <input id="room-name-input" type="text" class="input-field flex-1" value="${escapeHtml(room.name)}" ${_myRole !== 'owner' ? 'disabled' : ''}>
            ${_myRole === 'owner' ? '<button id="btn-save-name" class="btn btn-primary px-4 py-2.5 text-sm shrink-0">저장</button>' : ''}
          </div>
        </div>
      </div>

      <!-- 멤버 목록 -->
      <div>
        <div class="section-header mb-2">멤버 (${members.length}명)</div>
        <div class="space-y-1">
          ${owner ? renderMember(owner, '방장', 'badge-owner') : ''}
          ${subAdmins.map((m: any) => renderMember(m, '부방장', 'badge-admin')).join('')}
          ${regulars.map((m: any) => renderMember(m, '', '')).join('')}
        </div>
      </div>

      <!-- 초대 QR 코드 -->
      <div>
        <div class="section-header mb-2">초대 QR 코드</div>
        <div class="surface-secondary rounded-xl p-4 text-center">
          <img id="qr-image" src="" alt="QR Code" class="mx-auto w-48 h-48" style="display:none" onload="this.style.display='inline-block'">
          <div id="qr-loading" class="py-8 text-tertiary text-sm">QR 코드 생성 중...</div>
          <p class="text-xs text-tertiary mt-3">스캔하여 채팅방에 참가하세요</p>
        </div>
      </div>
    </div>`;

  // QR 코드 생성
  const roomUrl = `${window.location.origin}/rooms`;
  const qrUrl = `/api/proxy/qr?data=${encodeURIComponent(roomUrl)}&size=200&color=%232d6a4f`;
  function loadQrCode() {
    const loading = document.getElementById('qr-loading');
    const img = document.getElementById('qr-image') as HTMLImageElement;
    if (loading) { loading.textContent = 'QR 코드 생성 중...'; loading.style.display = ''; }
    if (img) {
      if (img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
      img.style.display = 'none';
    }
    fetch(qrUrl).then(async r => {
      const svg = await r.text();
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      if (img) { img.src = URL.createObjectURL(blob); img.style.display = 'inline-block'; if (loading) loading.style.display = 'none'; }
    }).catch(() => {
      if (loading) {
        loading.innerHTML = `<span class="text-red-500">QR 코드 생성 실패</span> <button type="button" id="btn-retry-qr" class="ml-2 text-kalpha-600 font-semibold hover:underline">재시도</button>`;
        document.getElementById('btn-retry-qr')?.addEventListener('click', loadQrCode);
      }
    });
  }
  loadQrCode();

  document.getElementById('btn-save-name')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-save-name') as HTMLButtonElement;
    const name = (document.getElementById('room-name-input') as HTMLInputElement).value.trim();
    if (!name) return;
    btn.disabled = true; btn.textContent = '저장 중...';
    const token = localStorage.getItem('kalpha_token');
    try {
      const resp = await fetch(`/api/rooms/${_roomId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const json = await resp.json();
      if (json.success) toast('방 이름이 변경되었습니다.', 'success');
      else toast(json.error || '실패', 'error');
    } finally { btn.disabled = false; btn.textContent = '저장'; }
  });
}

function renderMember(m: any, roleLabel: string, roleClass: string): string {
  return `
    <div class="flex items-center gap-3 px-3 py-2.5 surface-secondary rounded-xl">
          <div class="w-10 h-10 ${getAvatarGradient(m.display_name || '')} rounded-full flex items-center justify-center shrink-0"><span class="text-white font-bold text-sm">${(m.display_name || '?').charAt(0)}</span></div>
      <div class="flex-1 min-w-0"><p class="text-sm font-semibold text-primary">${escapeHtml(m.display_name || '')}</p><p class="text-xs text-tertiary">@${escapeHtml(m.username || '')}</p></div>
      ${roleLabel ? `<span class="badge-role ${roleClass} shrink-0">${roleLabel}</span>` : ''}
    </div>`;
}
